import type { Group, IdentityPermission, Role, User } from '@/iam/models';

const adminRoleHints = ['admin', 'global', 'platform'];

export const allIdentityPermissions: IdentityPermission[] = [
  'USER_VIEW',
  'USER_CREATE',
  'USER_EDIT',
  'USER_DELETE',
  'USER_INVITE',
  'USER_ASSIGN_ROLE',
  'USER_ASSIGN_PRODUCT',
  'USER_ASSIGN_GROUP',
  'GROUP_VIEW',
  'GROUP_CREATE',
  'GROUP_EDIT',
  'GROUP_DELETE',
  'GROUP_ASSIGN_USERS',
  'GROUP_ASSIGN_ROLES',
  'GROUP_ASSIGN_PRODUCTS',
  'ROLE_VIEW',
  'ROLE_CREATE',
  'ROLE_EDIT',
  'ROLE_DELETE',
  'ROLE_ASSIGN',
  'PRODUCT_VIEW',
  'PRODUCT_CREATE',
  'PRODUCT_EDIT',
  'PRODUCT_DELETE',
  'PRODUCT_ASSIGN_ROLE',
  'NOTIFICATIONS_VIEW',
  'AUDIT_VIEW',
  'AUDIT_EXPORT',
  'AUDIT_DETAILS',
  'AUDIT_ADMIN',
  'FEATURE_FLAGS_VIEW',
  'FEATUREFLAG_VIEW',
  'FEATUREFLAG_CREATE',
  'FEATUREFLAG_EDIT',
  'FEATUREFLAG_DELETE',
  'FEATUREFLAG_DEPLOY',
  'FEATUREFLAG_OVERRIDE',
  'SYSTEM_SETTINGS_VIEW',
  'SYSTEM_VIEW',
  'SYSTEM_EDIT',
  'SYSTEM_BRANDING',
  'SYSTEM_SECURITY',
  'SYSTEM_NOTIFICATION',
  'API_CONFIGURATION_VIEW',
  'API_VIEW',
  'API_CREATE',
  'API_EDIT',
  'API_DELETE',
  'API_ROTATE_KEYS',
  'API_WEBHOOK_MANAGE',
  'API_RATE_LIMIT_EDIT',
  'DOCUMENTATION_VIEW',
  'DOCUMENTATION_ADMIN',
  'DOCUMENTATION_EDIT',
  'SUPPORT_CREATE',
  'EMBEDDED_APPLICATIONS_VIEW',
];

function isElevatedAdminRole(roleName: string): boolean {
  const normalized = roleName.toLowerCase();
  return adminRoleHints.some((hint) => normalized.includes(hint));
}

export function resolveRolePermissions(
  roleNames: string[],
  rolesCatalog: Role[] = [],
): IdentityPermission[] {
  if (roleNames.some(isElevatedAdminRole)) {
    return allIdentityPermissions;
  }

  const permissions = new Set<IdentityPermission>();

  roleNames.forEach((roleName) => {
    const matchedRole = rolesCatalog.find(
      (catalogRole) =>
        catalogRole.name.toLowerCase() === roleName.toLowerCase(),
    );

    if (matchedRole) {
      matchedRole.permissions.forEach((permission) =>
        permissions.add(permission),
      );
      return;
    }

    if (allIdentityPermissions.includes(roleName as IdentityPermission)) {
      permissions.add(roleName as IdentityPermission);
    }
  });

  return [...permissions];
}

export function resolveEffectivePermissions(params: {
  user: Pick<User, 'roles' | 'permissions' | 'groups'>;
  groups?: Group[];
  rolesCatalog?: Role[];
}): IdentityPermission[] {
  const { user, groups = [], rolesCatalog = [] } = params;
  const effective = new Set<IdentityPermission>(user.permissions ?? []);

  const directPermissions = resolveRolePermissions(user.roles, rolesCatalog);
  directPermissions.forEach((permission) => effective.add(permission));

  user.groups.forEach((groupId) => {
    const group = groups.find((item) => item.id === groupId);

    if (!group) {
      return;
    }

    group.permissions.forEach((permission) => effective.add(permission));
    resolveRolePermissions(group.assignedRoles, rolesCatalog).forEach(
      (permission) => effective.add(permission),
    );
  });

  return [...effective];
}
